import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FiDownload, FiX } from 'react-icons/fi';

// Add type declaration for navigator.standalone
declare global {
  interface Navigator {
    standalone?: boolean;
  }
}

export default function PWAPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    // Check various conditions that would prevent showing the prompt
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    const hasDeclined = localStorage.getItem('pwaPromptDeclined') === 'true';
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone;
    const isInIframe = window.self !== window.top;

    // Don't show prompt if:
    // 1. Already installed
    // 2. Previously declined
    // 3. Not on a mobile device
    // 4. Already in standalone mode
    // 5. Running in an iframe
    if (
      !isInstalled &&
      !hasDeclined &&
      isMobile &&
      !isStandalone &&
      !isInIframe
    ) {
      const handleBeforeInstallPrompt = (e: any) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowPrompt(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      return () => {
        window.removeEventListener(
          'beforeinstallprompt',
          handleBeforeInstallPrompt,
        );
      };
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setShowPrompt(false);
        } else {
          // Store declined choice if user cancels the install prompt
          localStorage.setItem('pwaPromptDeclined', 'true');
          setShowPrompt(false);
        }
      } catch (error) {
        console.error('Error during PWA installation:', error);
        setShowPrompt(false);
      } finally {
        setDeferredPrompt(null);
      }
    }
  };

  const handleDecline = () => {
    // Store user's choice when they click "Maybe Later"
    localStorage.setItem('pwaPromptDeclined', 'true');
    setShowPrompt(false);
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className='fixed right-4 bottom-4 left-4 z-50 md:right-4 md:left-auto md:w-96'
        >
          <div className='rounded-xl border border-gray-700/50 bg-gray-800/95 p-4 shadow-lg backdrop-blur-sm'>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className='mb-2 font-semibold text-lg text-white'>
                  Install Boomer App
                </h3>
                <p className='mb-4 text-gray-300 text-sm'>
                  Install our app for a better experience and quick access to
                  all features!
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleInstall}
                    className='flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 font-medium text-sm text-white transition-colors hover:bg-blue-600'
                  >
                    <FiDownload /> Install
                  </button>
                  <button
                    onClick={handleDecline}
                    className='rounded-lg bg-gray-700/50 px-4 py-2 font-medium text-gray-300 text-sm transition-colors hover:bg-gray-700/70 hover:text-white'
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
              <button
                onClick={handleDecline}
                className='p-1 text-gray-400 hover:text-white'
              >
                <FiX size={20} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
